/**
 * A superfície viva da arena atual — uma só, para o jogo inteiro.
 *
 * O modelo do terreno (o que cada piso faz, como uma arena é sorteada) mora em
 * `@shared/arenaTerrain`, porque o servidor precisa dele igualzinho. O que mora
 * aqui é a parte que só existe no cliente: **qual** arena está no chão agora, as
 * cores com que ela é desenhada, e um contador de versão para o React saber que
 * ela mudou.
 *
 * O canal vive fora do React de propósito. Ele é lido em `useFrame` pelo tanque
 * do jogador, por cada bot, por cada jogador remoto e pelos efeitos — dezenas de
 * vezes por segundo. Guardá-lo num store re-renderizaria a cena inteira a cada
 * onda, e re-renderizar a cena não muda nada do que se vê: o que muda é o que os
 * loops de frame leem.
 *
 * O contador `version`, esse sim, é reativo — e é o único. Quem precisa
 * redesenhar quando a arena troca (as manchas no chão, os efeitos presos ao
 * tanque) observa o contador; quem precisa consultar o piso por frame lê o
 * canal. Antes o render das manchas dependia do número da onda: no
 * multijogador e no Battle Royale a onda nunca muda, então a arena trocava e as
 * manchas continuavam as da partida anterior — ou não apareciam nunca.
 */

import { create } from "zustand";

import {
  SURFACE_PROPS,
  SurfaceMask,
  type HazardType,
  type HazardZone,
  type SurfaceProps,
  type SurfaceType,
} from "@shared/arenaTerrain";

export type {
  ArenaArchetype,
  HazardType,
  HazardZone,
  SafeSpot,
  SurfaceProps,
  SurfaceStatus,
  SurfaceType,
  TerrainMode,
  TerrainPlan,
} from "@shared/arenaTerrain";

export {
  ARCHETYPES,
  frameRateAdjusted,
  getArchetype,
  glideDecay,
  hazardsPresent,
  isInsideHazard,
  planArenaTerrain,
  pushOutOfHazards,
  SURFACE_PROPS,
  terrainSeed,
} from "@shared/arenaTerrain";

/**
 * Cores de render.
 *
 * O emissivo soma direto na cor final, sem depender da luz — `intensidade ×
 * cor` acima de ~0,8 satura os canais e a mancha vira um borrão claro. Foi o
 * que aconteceu com a lava a 1,35 de um laranja claro: numa arena de deserto
 * ela saía cor de creme, indistinguível de areia. Agora a lava é vermelha na
 * base, o emissivo é curto e o brilho quem dá é o bloom que a cena já aplica.
 */
export const SURFACE_COLORS: Record<HazardType, { base: string; edge: string; emissive: number }> = {
  water: { base: "#1d5f8f", edge: "#5fc8ff", emissive: 0.12 },
  lava: { base: "#6b1200", edge: "#ff3d00", emissive: 0.55 },
  ice: { base: "#9fd8ec", edge: "#e8fbff", emissive: 0.18 },
  mud: { base: "#4a3a22", edge: "#7d6438", emissive: 0.04 },
};

interface LiveSurface {
  zones: readonly HazardZone[];
  mask: SurfaceMask | null;
  active: boolean;
}

export const arenaSurface: LiveSurface = {
  zones: [],
  mask: null,
  active: false,
};

interface ArenaSurfaceSignal {
  /** Sobe a cada troca de arena. Quem desenha o terreno observa isto. */
  version: number;
  /** Há terreno especial no chão agora? */
  active: boolean;
  bump: (active: boolean) => void;
}

export const useArenaSurfaceSignal = create<ArenaSurfaceSignal>((set) => ({
  version: 0,
  active: false,
  bump: (active) => set((state) => ({ version: state.version + 1, active })),
}));

/** Rasteriza as zonas numa grade e ativa a consulta O(1). */
export function setArenaSurface(zones: readonly HazardZone[], arenaSize: number): void {
  if (zones.length === 0) {
    clearArenaSurface();
    return;
  }

  arenaSurface.mask = new SurfaceMask(zones, arenaSize);
  arenaSurface.zones = arenaSurface.mask.zones;
  arenaSurface.active = true;
  useArenaSurfaceSignal.getState().bump(true);
}

export function clearArenaSurface(): void {
  const wasActive = arenaSurface.active;
  arenaSurface.zones = [];
  arenaSurface.mask = null;
  arenaSurface.active = false;
  // Sem terreno antes e sem terreno agora não é notícia para ninguém: bater o
  // contador aqui faria toda troca de tela (menu, lobby, fim de partida)
  // remontar o render do terreno à toa.
  if (wasActive) useArenaSurfaceSignal.getState().bump(false);
}

/** Superfície em (x, z). Sempre "ground" quando não há terreno ativo. */
export function sampleSurface(x: number, z: number): SurfaceType {
  const mask = arenaSurface.mask;
  if (!mask) return "ground";
  return mask.sample(x, z);
}

export function sampleSurfaceProps(x: number, z: number): SurfaceProps {
  return SURFACE_PROPS[sampleSurface(x, z)];
}
